import { ItemRarity, EquipmentSlot } from '@shared/types';
import {
  GameItem,
  createEquipmentItem,
  createEquipmentFromBase,
  createCurrencyDrop,
  EQUIPMENT_BASES
} from './Item';
import {
  rollLegendaryDrop,
  LegendaryItem
} from './LegendaryItems';
import { getAllSets } from './SetItemSystem';
import { pickRandomBase, getBaseById } from './BaseItemRegistry';

export interface LootTableEntry {
  type: 'equipment' | 'currency' | 'legendary';
  baseId?: string;
  weight: number;
  minAmount?: number;
  maxAmount?: number;
}

export type LootItem = GameItem | LegendaryItem;

export interface LootTableConfig {
  entries: LootTableEntry[];
  dropChance: number; // 0-1
  minDrops: number;
  maxDrops: number;
  rarityWeights: Record<ItemRarity, number>;
  setItemDropChance?: number; // 0-1, chance to drop a set item per roll (default: 0)
}

export class LootTable {
  private entries: LootTableEntry[];
  private dropChance: number;
  private minDrops: number;
  private maxDrops: number;
  private rarityWeights: Record<ItemRarity, number>;
  private totalWeight: number;
  private setItemDropChance: number;

  constructor(config: LootTableConfig) {
    this.entries = config.entries;
    this.dropChance = config.dropChance;
    this.minDrops = config.minDrops;
    this.maxDrops = config.maxDrops;
    this.rarityWeights = config.rarityWeights;
    this.setItemDropChance = config.setItemDropChance || 0;

    this.totalWeight = this.entries.reduce((sum, e) => sum + e.weight, 0);
  }

  roll(itemLevel: number, luck: number = 0, playerClass: string = 'alp'): LootItem[] {
    const drops: LootItem[] = [];

    // Check if anything drops
    if (Math.random() > this.dropChance) {
      return drops;
    }

    // Determine number of drops
    const numDrops = Math.floor(
      this.minDrops + Math.random() * (this.maxDrops - this.minDrops + 1)
    );

    for (let i = 0; i < numDrops; i++) {
      const item = this.rollSingleItem(itemLevel, luck, playerClass);
      if (item) {
        drops.push(item);
      }
    }

    return drops;
  }

  private rollSingleItem(itemLevel: number, luck: number, playerClass: string): LootItem | null {
    // Try set item drop first (if configured)
    if (this.setItemDropChance > 0 && Math.random() < this.setItemDropChance) {
      const setItem = rollSetItemDrop(itemLevel);
      if (setItem) return setItem;
    }

    // Pick entry based on weight
    let roll = Math.random() * this.totalWeight;
    let selectedEntry: LootTableEntry | null = null;

    for (const entry of this.entries) {
      roll -= entry.weight;
      if (roll <= 0) {
        selectedEntry = entry;
        break;
      }
    }

    if (!selectedEntry) {
      selectedEntry = this.entries[this.entries.length - 1];
    }

    if (selectedEntry.type === 'currency') {
      const min = selectedEntry.minAmount || 1;
      const max = selectedEntry.maxAmount || 10;
      const amount = Math.floor(min + Math.random() * (max - min + 1));
      return createCurrencyDrop(amount);
    }

    if (selectedEntry.type === 'equipment') {
      const rarity = this.rollRarity(luck);

      // If legendary rarity is rolled, try to drop a unique legendary item
      if (rarity === 'legendary') {
        const legendaryItem = rollLegendaryDrop(itemLevel, playerClass, luck);
        if (legendaryItem) {
          return legendaryItem;
        }
        // Fall back to rare item if no legendary available
        return this.createRandomEquipment('rare', itemLevel, selectedEntry.baseId);
      }

      return this.createRandomEquipment(rarity, itemLevel, selectedEntry.baseId);
    }

    return null;
  }

  /**
   * Create a random equipment item using ilvl-based base selection from the registry.
   * Falls back to legacy system if no registry bases are available.
   */
  private createRandomEquipment(rarity: ItemRarity, itemLevel: number, fixedBaseId?: string): GameItem | null {
    if (fixedBaseId) {
      return createEquipmentItem(fixedBaseId, rarity, itemLevel);
    }

    // Pick a random slot then pick a random base for that slot at this level
    const slots: EquipmentSlot[] = ['head', 'chest', 'gloves', 'belt', 'boots', 'weapon', 'offhand', 'amulet', 'ring1', 'cape'];
    const slot = slots[Math.floor(Math.random() * slots.length)];
    const base = pickRandomBase(slot, itemLevel);

    if (base) {
      return createEquipmentFromBase(base, rarity, itemLevel);
    }

    // Fallback to legacy
    const baseId = this.getRandomBaseId();
    return createEquipmentItem(baseId, rarity, itemLevel);
  }

  private rollRarity(luck: number): ItemRarity {
    // Luck increases chance for better rarities
    const adjustedWeights = { ...this.rarityWeights };

    // Luck bonus (each point of luck increases rare+ chance by small amount)
    const luckBonus = luck * 0.01;
    adjustedWeights.rare = (adjustedWeights.rare || 0) * (1 + luckBonus);
    adjustedWeights.set = (adjustedWeights.set || 0) * (1 + luckBonus * 1.5);
    adjustedWeights.legendary = (adjustedWeights.legendary || 0) * (1 + luckBonus * 2);

    const total = Object.values(adjustedWeights).reduce((a, b) => a + b, 0);
    let roll = Math.random() * total;

    for (const [rarity, weight] of Object.entries(adjustedWeights)) {
      roll -= weight;
      if (roll <= 0) {
        return rarity as ItemRarity;
      }
    }

    return 'normal';
  }

  private getRandomBaseId(): string {
    const baseIds = Object.keys(EQUIPMENT_BASES);
    return baseIds[Math.floor(Math.random() * baseIds.length)];
  }
}

/**
 * Roll for a random set item drop.
 * Picks a random set, then a random piece from that set, and creates a rare-quality item.
 */
function rollSetItemDrop(itemLevel: number): LootItem | null {
  const sets = getAllSets();
  if (sets.length === 0) return null;

  // Pick a random set
  const set = sets[Math.floor(Math.random() * sets.length)];

  // Pick a random piece from that set
  const pieceBaseId = set.pieces[Math.floor(Math.random() * set.pieces.length)];

  // Check the base exists and is level-appropriate (try registry first, fallback to legacy)
  const registryBase = getBaseById(pieceBaseId);
  const legacyBase = EQUIPMENT_BASES[pieceBaseId];
  const baseLevel = registryBase?.level ?? legacyBase?.level;
  if (baseLevel === undefined) return null;
  if (baseLevel > itemLevel + 5) return null; // Don't drop items too high above player level

  // Set items always drop as rare quality
  return createEquipmentItem(pieceBaseId, 'rare', itemLevel);
}

// Predefined loot tables
export const LOOT_TABLES = {
  // Basic enemy (like Körmös)
  basic: new LootTable({
    entries: [
      { type: 'currency', weight: 70, minAmount: 1, maxAmount: 5 },
      { type: 'equipment', weight: 30 },
    ],
    dropChance: 0.5,       // 50% drop chance (was 60% - less loot clutter)
    minDrops: 1,
    maxDrops: 1,            // Only 1 drop (was 1-2 - reduces screen clutter)
    rarityWeights: {
      normal: 70,
      magic: 25,
      rare: 4,
      set: 0.9,
      legendary: 0.1,
    },
  }),

  // Elite enemy
  elite: new LootTable({
    entries: [
      { type: 'currency', weight: 45, minAmount: 5, maxAmount: 25 },
      { type: 'equipment', weight: 55 },
    ],
    dropChance: 0.9,
    minDrops: 1,
    maxDrops: 3,
    rarityWeights: {
      normal: 35,
      magic: 40,
      rare: 18,
      set: 5,
      legendary: 2,
    },
    setItemDropChance: 0.03, // 3% chance per drop roll
  }),

  // Boss enemy
  boss: new LootTable({
    entries: [
      { type: 'currency', weight: 20, minAmount: 50, maxAmount: 200 },
      { type: 'equipment', weight: 80 },
    ],
    dropChance: 1.0,
    minDrops: 3,
    maxDrops: 6,
    rarityWeights: {
      normal: 5,
      magic: 20,
      rare: 40,
      set: 25,
      legendary: 10,       // 10% legendary chance on boss (was 8%)
    },
    setItemDropChance: 0.10, // 10% chance per drop roll (was 8%)
  }),

  // Act 1 basic (early game - more normal items, less gold)
  act1_basic: new LootTable({
    entries: [
      { type: 'currency', weight: 75, minAmount: 1, maxAmount: 3 },
      { type: 'equipment', weight: 25 },
    ],
    dropChance: 0.45,
    minDrops: 1,
    maxDrops: 1,
    rarityWeights: {
      normal: 75,
      magic: 20,
      rare: 4,
      set: 0.8,
      legendary: 0.2,
    },
  }),

  // Act 1 elite (slightly better than basic)
  act1_elite: new LootTable({
    entries: [
      { type: 'currency', weight: 45, minAmount: 3, maxAmount: 15 },
      { type: 'equipment', weight: 55 },
    ],
    dropChance: 0.85,
    minDrops: 1,
    maxDrops: 2,
    rarityWeights: {
      normal: 40,
      magic: 38,
      rare: 16,
      set: 4.5,
      legendary: 1.5,
    },
    setItemDropChance: 0.02,
  }),

  // Act 1 boss (Albasti Kralicesi - guaranteed good loot)
  act1_boss: new LootTable({
    entries: [
      { type: 'currency', weight: 15, minAmount: 40, maxAmount: 120 },
      { type: 'equipment', weight: 85 },
    ],
    dropChance: 1.0,
    minDrops: 4,
    maxDrops: 7,
    rarityWeights: {
      normal: 3,
      magic: 15,
      rare: 42,
      set: 28,
      legendary: 12,
    },
    setItemDropChance: 0.12,
  }),
};
